import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Styled Components
const GallerySection = styled.section`
  padding: 5rem 0;
  background-color: #f8fafc;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const GalleryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SliderWrapper = styled.div`
  margin-bottom: 2rem;
  
  .slick-slide {
    padding: 0 10px;
  }
  
  .slick-prev, .slick-next {
    z-index: 1;
    width: 40px;
    height: 40px;
    
    &:before {
      font-size: 40px;
      opacity: 0.8;
      color: #1e3a8a;
    }
  }
  
  .slick-prev {
    left: -10px;
  }
  
  .slick-next {
    right: -10px;
  }
`;

const ImageContainer = styled.div`
  height: 250px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:hover::after {
    opacity: 0.4;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ViewAllButton = styled(Link)`
  display: block;
  width: fit-content;
  margin: 2rem auto 0;
  background-color: #1e3a8a;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    background-color: #1e40af;
    transform: translateY(-2px);
    color: white;
  }
`;



const GalleryPreview = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const galleryCollection = collection(db, 'galleryImages');
        const gallerySnapshot = await getDocs(galleryCollection);
        const galleryData = gallerySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGalleryImages(galleryData);
      } catch (error) {
        console.error('Error fetching gallery images:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGalleryImages();
  }, []);
  // Settings for the slider
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  return (
    <GallerySection id="gallery-preview">
      <SectionTitle>Our Gallery</SectionTitle>
      <GalleryContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading gallery images...</p>
          </div>
        ) : galleryImages.length > 0 ? (
          <SliderWrapper>
            <Slider {...settings}>
              {galleryImages.map((image) => (
                <div key={image.id}>
                  <ImageContainer>
                    <GalleryImage src={image.url} alt={`Gallery - ${image.title || image.category}`} />
                  </ImageContainer>
                </div>
              ))}
            </Slider>
          </SliderWrapper>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No gallery images available. Add some in the admin panel.</p>
          </div>
        )}
        <ViewAllButton to="/gallery">View All</ViewAllButton>
      </GalleryContainer>
    </GallerySection>
  );
};

export default GalleryPreview;
